import { Button } from "components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import { ArrowRight, Check, HeadphonesIcon, MessageCircle } from "lucide-react";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "components/ui/carousel";
import { Link } from "react-router-dom";
import { GetList } from "types/data-content";

interface IProductPage {
  pageData: GetList[];
}

const ProductPage = ({ pageData }: IProductPage) => {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-primary">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-4 py-[32px] md:py-10 sm:py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left animate-fade-in">
              <span className="inline-flex items-center rounded-full border border-white/30 bg-white/10 px-4 py-1 text-sm font-semibold text-white mb-4">
                {pageData[0].caseBadge || "Projeto Demo"}
              </span>

              {pageData[0].clientName && (
                <p className="text-[14px] md:text-base text-white/90 mb-3">
                  Cliente: <strong>{pageData[0].clientName}</strong> -{" "}
                  <a
                    href={pageData[0].link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline underline-offset-4 hover:text-white"
                  >
                    site no ar
                  </a>
                </p>
              )}

              <h1 className="text-[20px] md:text-[38px] font-bold text-white mb-6">
                <span className="text-white leading-tight">
                  {pageData[0].titlePage}
                </span>
              </h1>
              <div
                className="text-[16px] text-white/90 mb-8 max-w-2xl"
                dangerouslySetInnerHTML={{ __html: pageData[0].description }}
              />
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <a
                  href={pageData[0].link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center text-[16px] md:text-lg px-5 py-3 bg-white rounded-lg text-[#17579e] 
                transition-all duration-500 ease-out
                 hover:shadow-lg hover:scale-105"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  {pageData[0].caseCtaLabel ||
                    (pageData[0].caseBadge === "Projeto Real / Cliente"
                      ? "Visitar site"
                      : "Ver Demonstração")}
                </a>
              </div>
            </div>
            <div className="animate-float">
              <img
                src={pageData[0].image}
                alt={pageData[0].titlePage}
                className="w-full h-auto max-h-[368px] rounded-2xl shadow-2xl object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="beneficios" className="py-8 sm:py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8  sm:mb-16 animate-slide-up">
            <h2 className="text-[22px] md:text-4xl font-bold text-foreground mb-2 md:mb-4">
              Principais Benefícios
            </h2>
            <p className="text-[14px] md:text-xl text-muted-foreground max-w-2xl mx-auto">
              {pageData[0].beneficiosDescription}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
            {pageData[0]?.features?.map((feature, index) => (
              <Card
                key={index}
                variant="feature"
                className="animate-scale-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <CardHeader>
                  <div className="flex items-center space-x-4">
                    <div className="p-3 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors">
                      {feature.icon}
                    </div>
                    <CardTitle className="text-[17px] md:text-xl">
                      {feature.title}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-[14px] md:text-base">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Demo Section */}
      {!pageData[0].video && pageData[0].carousel && (
        <section
          id="demonstracao"
          className="pt-5 pb-14 bg-muted/30 max-w-[100vw] w-full"
        >
          <div className="container mx-auto px-4">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Veja Como Funciona
              </h2>
              <p className="text-xl text-muted-foreground">
                {pageData[0].descricaoFuncionamento}
              </p>
            </div>

            <div className="max-w-6xl w-full mx-auto">
              <Carousel
                className="w-full"
                opts={{ align: "start", loop: true }}
              >
                <CarouselContent className="flex flex-nowrap -ml-2">
                  {pageData[0]?.imagesCarousel?.map((demo, index) => (
                    <CarouselItem
                      key={index}
                      className="pl-2 basis-[100%] flex-shrink-0"
                    >
                      <Card
                        variant="modern"
                        className="overflow-hidden max-h-[500px]"
                      >
                        <CardContent className="p-0">
                          <div className="bottom-0 left-0 right-0 bg-gradient-to-t bg-[#08080899] p-5 py-3">
                            <h3 className="text-xl text-white mb-1">
                              {demo.label}
                            </h3>
                          </div>
                          <div className="relative">
                            <img
                              src={demo.imgPath}
                              alt={demo.label}
                              className="w-full h-auto max-w-full"
                            />
                          </div>
                        </CardContent>
                      </Card>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious className="left-4" />
                <CarouselNext className="right-4" />
              </Carousel>
            </div>

            <div className="text-center mt-8 max-w-full">
              <Button
                asChild
                variant="hero"
                size="lg"
                className="text-lg px-8 py-4"
              >
                <a
                  href={pageData[0].link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="max-w-full"
                >
                  <ArrowRight className="w-5 h-5 mr-2" />
                  {pageData[0].caseDemoCtaLabel ||
                    (pageData[0].caseBadge === "Projeto Real / Cliente"
                      ? "Visitar site"
                      : "Acessar Demonstração Completa")}
                </a>
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Investment Section */}
      <section
        id="investimento"
        className="py-8 sm:py-20 bg-gradient-primary relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-4">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-[19px] md:text-3xl md:text-4xl font-bold text-white mb-4">
              Invista no Crescimento do Seu Negócio 🚀
            </h2>
            <p className="text-[16px] md:text-xl text-white/90 mb-2">
              Parcelamos até{" "}
              <strong className="text-white">12x no cartão de crédito</strong>
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <Card variant="modern" className="bg-white/95 backdrop-blur-sm">
              <CardHeader className="text-center pb-2 md:pb-8">
                <CardTitle className="text-[19px] md:text-3xl text-foreground mb-2 md:mb-4">
                  Pacote Completo Incluído
                </CardTitle>
                {/* <CardDescription className="text-lg">
                </CardDescription> */}
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4 md:gap-6">
                  {pageData[0]?.includedFeatures?.map((feature, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <Check className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                      <span className="text-[15px] md:[inherit] text-foreground font-medium">
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="bg-gradient-primary/10 rounded-lg pt-2 md:p-6 text-center mt-0 md:mt-8">
                  <h3 className="text-[20px] leading-tight md:leading-8 md:text-2xl font-bold text-foreground mb-2">
                    Pronto para dar o próximo passo?
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    Entre em contato agora pelo WhatsApp ou agende uma reunião
                    sem compromisso por vídeo, estamos prontos para entender
                    suas necessidades e criar algo incrível para você!
                  </p>

                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button
                      asChild
                      variant="whatsapp"
                      size="lg"
                      className="text-[14px] md:text-lg px-8 py-4"
                    >
                      <a
                        href="https://api.whatsapp.com/send?phone=5511964914546"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-auto"
                      >
                        <MessageCircle className="w-5 h-5 mr-2" />
                        Falar no WhatsApp
                      </a>
                    </Button>
                    <Button
                      asChild
                      variant="hero"
                      size="lg"
                      className="text-[14px] md:text-lg px-8 py-4"
                    >
                      <Link to="/agendar" className="w-auto">
                        <HeadphonesIcon className="w-5 h-5 mr-2" />
                        Agendar Reunião
                      </Link>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductPage;
